import { PIMSType } from "@common/utils/types";

// --- Step IDs ---

export type SetupStepId =
    | "name"
    | "role"
    | "clinic-name"
    | "clinic-confirm"
    | "clinic-pick"
    | "clinic-manual"
    | "pims"
    | "mic";

// --- Clinic result from Google Places ---

export interface ClinicResult {
    name: string;
    formattedAddress: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone: string | null;
    website: string | null;
}

// --- Setup context (accumulates as user progresses) ---

export type RoleValue = (typeof ROLE_OPTIONS)[number]["value"];

export interface SetupContext {
    name: string;
    role: RoleValue | null;
    clinicName: string;
    clinicResult: ClinicResult | null;
    /** Raw PIMS enum value (e.g. "cornerstone") or "none", or null if not yet selected */
    selectedPims: PIMSType | "none" | null;
}

// --- Scout speech per step ---

export const SCOUT_SPEECH: Record<SetupStepId, { bold: string; sub: string }> = {
    name: { bold: "Hey! I'm Scout.", sub: "Let's get you set up." },
    role: { bold: "Nice to meet you, {name}!", sub: "What do you do?" },
    "clinic-name": { bold: "Great, {name}!", sub: "I'll look up your clinic." },
    "clinic-confirm": { bold: "Found it!", sub: "Is this your clinic?" },
    "clinic-pick": { bold: "I found a few!", sub: "Which one is yours?" },
    "clinic-manual": { bold: "No worries!", sub: "Let's set up your clinic details." },
    pims: { bold: "Saved your clinic!", sub: "One more thing." },
    mic: { bold: "Last step!", sub: "HappyDoc records your appointments." },
};

// --- Progress per step (maps to 5 dot indicators: 0=name, 1=role, 2=clinic, 3=pims, 4=mic) ---

export const STEP_PROGRESS: Record<SetupStepId, number> = {
    name: 0,
    role: 1,
    "clinic-name": 2,
    "clinic-confirm": 2,
    "clinic-pick": 2,
    "clinic-manual": 2,
    pims: 3,
    mic: 4,
};

export const PROGRESS_DOT_COUNT = 5;

// --- Role options ---

export const ROLE_OPTIONS = [
    { label: "Owner", value: "owner" },
    { label: "Manager", value: "manager" },
    { label: "Associate Doctor", value: "associate_doctor" },
    { label: "Tech", value: "tech" },
    { label: "Other", value: "other" },
] as const;

// --- PIMS options for the selection grid ---

export interface PimsOption {
    label: string;
    value: string;
}

export const PIMS_OPTIONS: PimsOption[] = [
    { label: "Cornerstone", value: PIMSType.CORNERSTONE },
    { label: "ezyVet", value: PIMSType.EZYVET },
    { label: "Vetspire", value: PIMSType.VETSPIRE },
    { label: "Shepherd", value: PIMSType.SHEPHERD },
    { label: "Avimark", value: PIMSType.AVIMARK },
    { label: "Impromed", value: PIMSType.IMPROMED },
    { label: "DaySmart", value: PIMSType.DAYSMART },
    { label: "Pulse", value: PIMSType.PULSE },
    { label: "Hippo", value: PIMSType.HIPPO },
    { label: "Instinct", value: PIMSType.INSTINCT },
    { label: "Provet Cloud", value: PIMSType.PROVET },
    { label: "Neo", value: PIMSType.NEO },
    { label: "NaVetor", value: PIMSType.NAVETOR },
    { label: "IntraVet", value: PIMSType.INTRAVET },
    { label: "Digitail", value: PIMSType.DIGITAIL },
    { label: "VetFM", value: PIMSType.VETFM },
    { label: "Woofware", value: PIMSType.WOOFWARE },
    { label: "Petware", value: PIMSType.PETWARE },
    { label: "Other", value: PIMSType.OTHER },
    { label: "I don't use one", value: "none" },
];
